/**
 * @license
 * Copyright 2018 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { WatchableValueInterface } from 'neuroglancer/trackable_value';
import { parseRGBColorSpecification, serializeColor } from 'neuroglancer/util/color';
import { hsvToRgb, rgbToHsv } from 'neuroglancer/util/colorspace';
import { RefCounted } from 'neuroglancer/util/disposable';
import { vec3 } from 'neuroglancer/util/geom';
import { viewer } from 'src/main';
import i18n from '../i18n/i18n';
import { ImageUserLayer } from '../image_user_layer';
import { ManagedUserLayer, UserLayer } from '../layer';
import { makeElement } from '../ui/layer_gene_table_tab';
import './message.less';

// dapi单选颜色控制

export class RadioColorWidget<Color extends vec3 | undefined = vec3> extends RefCounted {
  element: HTMLInputElement | HTMLDivElement;
  constructor(
    public model: WatchableValueInterface<Color>,
    public getDefaultColor: (() => vec3) = () => vec3.fromValues(1, 0, 0)) {
    super();
    this.initDom([{color: '#ffffff', text: 'gray'}, {color: '#0000ff', text: 'blue'}], serializeColor(vec3.fromValues(this.model.value![0], this.model.value![1], this.model.value![2])))
    this.setCheckedColor()
    this.registerDisposer(model.changed.add(() => {
      this.setCheckedColor()
    }));
  }
  
  setCheckedColor () {
    const color = serializeColor(vec3.fromValues(this.model.value![0], this.model.value![1], this.model.value![2]));
    const selectedDom = document.getElementById(color);
    selectedDom && ((selectedDom as HTMLInputElement).checked = true);
  }
  initDom (colorList: Array<any>, checkColor: string ) {
    const element = this.element = document.createElement('div')
    const label = document.createElement('span')
    label.innerText = `${i18n.color}`
    element.appendChild(label)
    const radioContent = document.createElement('div')
    element.classList.add('radio-color-widget')
    const name = `radioColor${new Date().getTime()}`
    for (let value of colorList) {
      const radioWrap = document.createElement('div')
      radioWrap.classList.add('radio-color-wrap')
      const input = document.createElement('input')
      input.type = 'radio'
      input.name = name
      input.id = value.color
      input.value = value.color
      if (checkColor === value.color) {
        input.checked = true
      }
      input.classList.add('ng-common-radio')
      input.addEventListener('change', (e:any) => {
        this.model.value = parseRGBColorSpecification(e.target.value) as Color;
        if(JSON.parse( JSON.stringify ( (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls )) !== '{}'){
          (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls['color'] = e.target.value;
        }else{
          (viewer.selectedLayer.layer as ManagedUserLayer)['shaderControls'] = {};
          (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls['color'] = e.target.value;
        }
      })
      const label = document.createElement('label')
      label.innerText = value.text
      // label.style.background = value
      radioWrap.appendChild(input)
      radioWrap.appendChild(label)
      radioContent.appendChild(radioWrap)
    }
    element.appendChild(radioContent)
  }
}
export class ColorWidget<Color extends vec3 | undefined = vec3> extends RefCounted {
  element: HTMLInputElement | HTMLDivElement;
  // color = document.createElement('input');
  constructor(
    public model: WatchableValueInterface<Color>,
    layer?: UserLayer,
    public getDefaultColor: (() => vec3) = () => vec3.fromValues(1, 0, 0)) {
    super();
    if(layer instanceof ImageUserLayer){
      if (layer.genename === 'DAPI') {
        this.element = (new RadioColorWidget(model)).element;
      } else {
        this.element = (new CustomColorWidget(model)).element;
      }
    }else{
      this.element = (new ngSelfColorWidget(model)).element;
    }
  }
}

class ngSelfColorWidget<Color extends vec3 | undefined = vec3> extends RefCounted {
  element: HTMLInputElement = document.createElement('input');
  constructor(
    public model: WatchableValueInterface<Color>,
    public getDefaultColor: (() => vec3) = () => vec3.fromValues(1, 0, 0)) {
    super();
    const { element } = this;
    element.type = 'color';
    element.classList.add('neuroglancer-color-widget');
    /** 原 只有一个input type=color 的按钮修改颜色 */
    element.addEventListener('change', () => this.updateModel());
    element.addEventListener('input', () => this.updateModel());
    element.addEventListener('wheel', event => {
      event.stopPropagation();
      event.preventDefault();
      this.adjustHueViaWheel(event);
    });
    /** 原 只有一个input type=color 的按钮修改颜色 */
    this.registerDisposer(model.changed.add(() => this.updateView()));
    this.updateView();
  }
  private getRGB() {
    return this.model.value ?? this.getDefaultColor();
  }
  private updateView() {
    this.element.value = serializeColor(this.getRGB());
  }
  private updateModel() {
    this.model.value = parseRGBColorSpecification(this.element.value) as Color;
    let _json = viewer.state.toJSON();
    let selectedLayer = 'color-widget-' + _json.selectedLayer.layer;
    const color = serializeColor(vec3.fromValues(this.model.value![0], this.model.value![1], this.model.value![2]));
    const color_widget: any = document.getElementsByClassName(selectedLayer);
    (viewer.selectedLayer.layer as ManagedUserLayer )['genecolor'] = color;
    for (let j = 0; j < color_widget.length; j++) {
      color_widget[j].style.backgroundColor = color;
    };
    const layer = viewer.layerManager.getLayerByName(_json.selectedLayer.layer);
    if(layer?.layer && layer?.layer.genetype === 'proteinimage'){
      _json.selectedGenes[_json.selectedLayer.layer] = color;
      viewer.saveSelectedKeyVal[_json.selectedLayer.layer][1] = color;
      viewer.allDataSource.changeProteinColor[_json.selectedLayer.layer] = color;
    }
  }

  adjustHueViaWheel(event: WheelEvent) {
    const rgb = this.getRGB();
    const temp = vec3.create();
    rgbToHsv(temp, rgb[0], rgb[1], rgb[2]);
    const { deltaY } = event;
    let hue = Math.round(temp[0] * 256);
    hue += (deltaY > 0 ? 1 : deltaY < 0 ? -1 : 0);
    hue = (hue + 256) % 256;
    temp[0] = hue / 256;
    hsvToRgb(temp, temp[0], temp[1], temp[2]);
    this.model.value = temp as Color;
  }
}

export class CustomColorWidget<Color extends vec3 | undefined = vec3> extends RefCounted {
  element = document.createElement('div');
  color_squer = makeElement('div', ['color-squer']);
  color_input_r = makeElement('input', ['color-input-r'],{'type': 'number'}) as HTMLInputElement;
  color_input_g = makeElement('input', ['color-input-g'],{'type': 'number'}) as HTMLInputElement;
  color_input_b = makeElement('input', ['color-input-b'],{'type': 'number'}) as HTMLInputElement;
  color_input_hex = makeElement('input', ['color-input-hex']) as HTMLInputElement;
  platterDom = document.createElement('canvas');        //  左边大框颜色
  global = document.createElement('canvas');            //  中间圆圈
  colorBar = document.createElement('canvas');          //  右边颜色带
  canvasAre = makeElement('div');
  canvasLeft = document.createElement('div');
  canvasRight = makeElement('div',['canvas-right'],{'style': 'display: flex;justify-content: space-between;padding: 1rem 0'});
  gCtx: CanvasRenderingContext2D = this.global.getContext('2d') as CanvasRenderingContext2D;
  ctx: CanvasRenderingContext2D = this.platterDom.getContext('2d') as CanvasRenderingContext2D;
  cCtx:CanvasRenderingContext2D = this.colorBar.getContext('2d') as CanvasRenderingContext2D;
  sR = 0;
  sG = 255;
  sB = 255;
  xScale = Math.ceil(this.platterDom.width / 128);//向上取整方便绘制矩形
  yScale = Math.ceil(this.platterDom.height / 128);//向上取整方便绘制矩形
  scaleWidth = this.platterDom.width / this.xScale;
  scaleHeight = this.platterDom.height / this.yScale;
  pickLineWidth = 16;
  pickLineY: number;
  isInitColorBar:boolean = false;
  pickPoint:{[k:string]: number} = {};
  default_color_arr = [{ id: 0, color: '#D20000', rgb: 'rgb(210,0,0)' }, { id: 1, color: '#FF0000', rgb: 'rgb(255,0,0)' }, { id: 2, color: '#FFBE00', rgb: 'rgb(255,190,0)' }, { id: 3, color: '#FEFF00', rgb: 'rgb(254,255,0)' },
  { id: 4, color: '#7ED332', rgb: 'rgb(126,211,50)' }, { id: 5, color: '#00B442', rgb: 'rgb(210,0,0)' }, { id: 6, color: '#00B2F6', rgb: 'rgb(0,180,60)' }, { id: 7, color: '#0070C6', rgb: 'rgb(0,178,246)' }, { id: 8, color: '#001F64', rgb: 'rgb(0,112,198)' }, { id: 9, color: '#7A26A6', rgb: 'rgb(0,31,100)' },
  ];
  defaultColor = document.createElement('div');
  constructor(
    public model: WatchableValueInterface<Color>,
    public getDefaultColor: (() => vec3) = () => vec3.fromValues(1, 0, 0)) {
    super();
    const { element, platterDom, global, colorBar, canvasLeft, color_input_r, color_input_g, color_input_b } = this;
    element.style.textAlign = 'center';
    element.addEventListener('click',(event:Event)=>{
      event.preventDefault()
    })
    platterDom.id = 'platterDom';
    global.id = 'global';
    colorBar.id = 'colorBar';
    canvasLeft.style.position = 'relative';
    canvasLeft.style.display = 'inline-block'
    this.initDom();
    this.initPlatter();
    this.draw();
    this.initBarHelper();
    this.colorBarDraw();
    this.makecolorlist();
    this.registerDisposer(model.changed.add(() => this.updateView()));
    this.updateView();
    color_input_r.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_r.blur();
      }
    });
    color_input_g.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_g.blur();
      }
    });
    color_input_b.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        color_input_b.blur();
      }
    });
    this.color_input_hex.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        this.color_input_hex.blur();
      }
    });
    color_input_r.addEventListener('blur',()=>{
      if(color_input_r.valueAsNumber > 255 || color_input_r.valueAsNumber < 0 || isNaN(color_input_r.valueAsNumber)) return
      this.changeSquerColor();
      const _json = viewer.state.toJSON();
      _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
      viewer.resetoreJson(_json);
    });
    color_input_g.addEventListener('blur',()=>{
      if(color_input_g.valueAsNumber > 255 || color_input_g.valueAsNumber < 0 || isNaN(color_input_g.valueAsNumber)) return
      this.changeSquerColor();
      const _json = viewer.state.toJSON();
      _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
      viewer.resetoreJson(_json);
    });
    color_input_b.addEventListener('blur',()=>{
      if(color_input_b.valueAsNumber > 255 || color_input_b.valueAsNumber < 0 || isNaN(color_input_b.valueAsNumber)) return
      this.changeSquerColor();
      const _json = viewer.state.toJSON();
      _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
      viewer.resetoreJson(_json);
    });
    this.color_input_hex.addEventListener('blur',()=>{
      const value = this.color_input_hex.value;
      const b = value.match(`^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$`);
      if(!b) return
      this.color_squer.style.backgroundColor = value;
      this.updateModel();
      this.updatePickLineGlobal( [this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber] );
      const _json = viewer.state.toJSON();
      _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
      viewer.resetoreJson(_json);
    });
  }
  changeSquerColor(){
    this.color_squer.style.backgroundColor = rgbToHex(this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber)
    this.updatePickLineGlobal( [this.color_input_r.valueAsNumber, this.color_input_g.valueAsNumber, this.color_input_b.valueAsNumber] );
    this.updateModel();
  }
  initDom(){
    const {platterDom, global, colorBar, canvasLeft, canvasRight, color_squer, color_input_hex, color_input_r, color_input_g, color_input_b, canvasAre} = this;
    platterDom.width = 240;
    platterDom.height = 180;
    global.width = 10;
    global.height = 10;
    colorBar.width = 184;
    colorBar.height = 25;
    canvasLeft.appendChild(platterDom)
    canvasLeft.appendChild(global);
    canvasRight.appendChild(colorBar);
    canvasRight.appendChild(color_squer);
    const bottom = makeElement('div', [], { 'style': 'display: flex;justify-content: space-between;' });
    const hex = makeElement('div', ['color-input']);
    const hexspan = makeElement('span', [], {}, `hex`);
    hex.appendChild(color_input_hex);
    hex.appendChild(hexspan);
    const ColorR = makeElement('div', ['color-input']);
    const ColorRspan = makeElement('span', [], {}, `R`);
    ColorR.appendChild(color_input_r);
    ColorR.appendChild(ColorRspan);
    const ColorG = makeElement('div', ['color-input']);
    const ColorGspan = makeElement('span', [], {}, `G`);
    ColorG.appendChild(color_input_g);
    ColorG.appendChild(ColorGspan);
    const ColorB = makeElement('div', ['color-input']);
    const ColorBspan = makeElement('span', [], {}, `B`);
    ColorB.appendChild(color_input_b);
    ColorB.appendChild(ColorBspan);
    bottom.appendChild(hex);
    bottom.appendChild(ColorR);
    bottom.appendChild(ColorG);
    bottom.appendChild(ColorB);
    canvasAre.appendChild(canvasLeft);
    canvasAre.appendChild(canvasRight);
    canvasAre.style.padding = '0 5px'
    this.element.appendChild(canvasAre)
    this.element.appendChild(bottom);
  }
  initPlatter(){
    var that = this;
    this.platterDom.onmousedown = handleMouseEvent;
    this.global.onmousedown = handleMouseEvent;
    function handleMouseEvent(e: MouseEvent) {
      dragGlobal(e);
      document.onmousemove = function (e) {
        dragGlobal(e);
      }
      document.onmouseup = function () {
        const _json = viewer.state.toJSON();
        _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
        viewer.resetoreJson(_json);
        document.onmousemove = null;
        document.onmouseup = null;
      }
    }
    function dragGlobal(e: MouseEvent) {
      var minX = Number( that.platterDom.getBoundingClientRect().left );
      var maxX = Number( minX + that.platterDom.width );
      var minY = Number( that.platterDom.getBoundingClientRect().top );
      var maxY = Number( minY + that.platterDom.height );
      var x = Number( e.x - that.global.width / 2 );
      var y = Number( e.y - that.global.height / 2 );
      if (x < minX) {
        x = minX;
      }
      else if (x > maxX) {
        x = maxX;
      }
      if (y < minY) {
        y = minY;
      }
      else if (y > maxY) {
        y = maxY;
      }
      var cx = x - that.platterDom.getBoundingClientRect().left;
      var cy = y -that.platterDom.getBoundingClientRect().top;
      that.global.style.left = cx - 5 + 'px';
      that.global.style.top = cy - 5 + "px";
      var col = that.getColor(cx, cy);
      that.pickPoint = { x: cx, y: cy };
      that.color_input_r.value = col.r.toString();
      that.color_input_g.value = col.g.toString();
      that.color_input_b.value = col.b.toString();
      that.color_input_hex.value = rgbToHex(col.r, col.g, col.b);
      that.color_squer.style.backgroundColor = rgbToHex(col.r, col.g, col.b);
      that.updateGlobal();
      that.updateModel();
      if(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0])(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
    }
  }
  updateGlobal () {
    this.gCtx!.clearRect(0, 0, this.global.width, this.global.height);
    this.gCtx!.strokeStyle = '#ffffff';
    this.gCtx!.lineWidth = 2;  
    this.gCtx!.beginPath();
    this.gCtx!.arc(5, 5, 5, 0,2*Math.PI);
    this.gCtx!.stroke();
  }
  draw() {
    this.xScale = Math.ceil(this.platterDom.width / 128);//向上取整方便绘制矩形
    this.yScale = Math.ceil(this.platterDom.height / 128);//向上取整方便绘制矩形
    this.scaleWidth = this.platterDom.width / this.xScale;
    this.scaleHeight = this.platterDom.height / this.yScale;
    //每个像素每个像素循环填充，计算太多，耗时严重。采取缩放，分块填充颜色的方式。当前约为128颜色级别。256以上会卡
    var xScale = this.xScale;
    var yScale = this.yScale;
    var w = this.scaleWidth;
    var h = this.scaleHeight;
    for (var x = 0; x < w; x++) {
      for (var y = 0; y < h; y++) {
        var col = this.getScaleColor(x, y);
        var color = 'rgb(' + col.r + ',' + col.g + ',' + col.b + ')';
        this.ctx!.fillStyle = color;
        this.ctx!.fillRect(x * xScale, y * yScale, xScale, yScale);
      }
    }
  }
  getScaleColor(scaleX: number, scaleY: number) {
    var w = this.scaleWidth;
    var h = this.scaleHeight;
    var r = 0;
    var g = 0;
    var b = 0;
    var sR = this.sR;
    var sG = this.sG;
    var sB = this.sB;
    var mid = sR > sG ? sR : sG;
    var max = mid > sB ? mid : sB;
    mid = mid > sB ? sB : mid;
    var min = sR + sG + sB - mid - max;
    var oneMaxXV = (255 - max) / w;
    // var oneMaxYV = (255 - max) / h;
    var oneMidXV = (255 - mid) / w;
    // var oneMidYV = (255 - mid) / h;
    var oneMinXV = (255 - min) / w;
    // var oneMinYV = (255 - min) / h;
    var midColor = 255 - scaleX * oneMidXV;
    var minColor = 255 - scaleX * oneMinXV;
    var maxColor = 255 - scaleX * oneMaxXV;
    var oneYTemp = midColor / h;
    var midC = Math.floor(midColor - scaleY * oneYTemp);
    oneYTemp = minColor / h;
    var minC = Math.floor(minColor - scaleY * oneYTemp);
    oneYTemp = maxColor / h;
    var maxC = Math.floor(maxColor - scaleY * oneYTemp);
    sR === max ? (r = maxC) : (sR === mid ? (r = midC) : (r = minC));
    sG === max ? (g = maxC) : (sG === mid ? (g = midC) : (g = minC));
    sB === max ? (b = maxC) : (sB === mid ? (b = midC) : (b = minC));
    return { r: r, g: g, b: b }
  }
  getColor(x: number, y: number) {
    return this.getScaleColor(x / this.xScale, y / this.yScale);
  }
  update(rgb:any) {
    this.sR = rgb.r;
    this.sG = rgb.g;
    this.sB = rgb.b;
    this.draw();
  }
  // 颜色带
  initBarHelper(){
    var that = this;
    this.colorBar.onmousedown = function (e: MouseEvent) {
      dragLine(e);
      document.onmousemove = function (e) {
        dragLine(e);
      }
      document.onmouseup = function () {
        const _json = viewer.state.toJSON();
        _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
        viewer.resetoreJson(_json);
        document.onmousemove = null;
        document.onmouseup = null;
      }
    }
    function dragLine(e: MouseEvent) {
      var left = Math.round(that.colorBar.getBoundingClientRect().left);
      var y = e.x - left;
      var col = that.getColorBarPickColor();
      that.updatePickLine(y, rgbToHex(col.r, col.g, col.b));
      that.update(col);
      col = that.getColor(that.pickPoint.x, that.pickPoint.y);
      that.color_input_r.value = col.r.toString();
      that.color_input_g.value = col.g.toString();
      that.color_input_b.value = col.b.toString();
      that.color_input_hex.value = rgbToHex(col.r, col.g, col.b);
      that.color_squer.style.backgroundColor = rgbToHex(col.r, col.g, col.b);
      that.updateGlobal();
      that.updateModel();
      if(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0])(document.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
    }
  }
  colorBarDraw(){
    this.drawRain();
  }
  drawRain(){
    var w = this.colorBar.width - this.pickLineWidth;
    var h = this.colorBar.height - this.pickLineWidth / 2;
    var ctx = this.cCtx;
    var offY = this.pickLineWidth / 2;
    for (var y = 0; y <= w; y++) {
      var col = this.getColorBarColor(y);
      var color = 'rgb(' + col.r + ',' + col.g + ',' + col.b + ")";
      ctx!.strokeStyle = color;
      ctx!.beginPath();
      ctx!.moveTo(y + offY, this.pickLineWidth / 2);
      ctx!.lineTo(y + offY, h);
      ctx!.stroke();
    }
  }
  getColorBarColor(y: number){
    var h = this.colorBar.width - this.pickLineWidth;;
    //每个像素颜色级别
    var oneYV = 255 / (h / 6);
    var r = 255;
    var g = 0;
    var b = 0;
    if (y <= h / 3) {
      //红-洋红
      if (y <= h / 6) {
        b = Math.floor(oneYV * y);
        if (b > 255) b = 255;
        r = 255;
      } else {
        //洋红-蓝
        r = 255 - Math.floor(oneYV * (y - h / 6));
        if (r < 0) r = 0;
        b = 255;
      }
      g = 0;
    } else if (y <= 2 * h / 3) {
      if (y < 3 * h / 6) {
        g = Math.floor(oneYV * (y - 2 * h / 6));
        if (g > 255) g = 255;
        b = 255;
      } else {
        b = 255 - Math.floor(oneYV * (y - 3 * h / 6));
        if (b < 0) b = 0;
        g = 255;
      }
      r = 0;
    } else {
      if (y < 5 * h / 6) {
        r = Math.floor(oneYV * (y - 4 * h / 6));
        if (r > 255) r = 255;
        g = 255;
      } else {
        g = 255 - Math.floor(oneYV * (y - 5 * h / 6));
        if (g < 0) g = 0;
        r = 255;
      }
      b = 0;
    }
    return { r: r, g: g, b: b };
  }
  drawPickLine (y: number, color: string) {
    this.pickLineY = y;
    var w = this.pickLineWidth;
    var ch = this.colorBar.height;
    var cw = this.colorBar.width;
    var ctx = this.cCtx;
    ctx.clearRect(0, 0, cw, ch);
    this.drawRain();
    ctx!.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(y + w / 2, ch / 2, 8, 0, 2 * Math.PI);
    ctx.fill();
    ctx!.fillStyle = color;
    ctx.beginPath();
    ctx.arc(y + w / 2, ch / 2, 6, 0, 2 * Math.PI);
    ctx.fill();
  }
  getColorBarPickColor () {
    return this.getColorBarColor(this.pickLineY);
  }
  updatePickLine (y: number, color: string) {
    var h = this.colorBar.width - this.pickLineWidth;
    var maxX = h;
    var minX = 0;
    if (y < minX) {
      y = minX;
    }
    else if (y > maxX) {
      y = maxX;
    }
    this.drawPickLine(y, color);
  }
  private getRGB() {
    return this.model.value ?? this.getDefaultColor();
  }
  private updateModel(value?:Color) {
    this.model.value = value ? value : parseRGBColorSpecification(this.color_squer.style.backgroundColor) as Color;
    let _json = viewer.state.toJSON();
    let selectedLayer = 'color-widget-' + _json.selectedLayer.layer;
    const color = serializeColor(vec3.fromValues(this.model.value![0], this.model.value![1], this.model.value![2]));
    const color_widget: any = document.getElementsByClassName(selectedLayer);
    for (let j = 0; j < color_widget.length; j++) {
      color_widget[j].style.backgroundColor = color;
    };
    const layer = viewer.layerManager.getLayerByName(_json.selectedLayer.layer);
    (viewer.selectedLayer.layer as ManagedUserLayer )['genecolor'] = color;
    if(layer?.layer && layer?.layer.genetype === 'proteinimage'){
      _json.selectedGenes[_json.selectedLayer.layer] = color;
      viewer.saveSelectedKeyVal[_json.selectedLayer.layer][1] = color;
      viewer.allDataSource.changeProteinColor[_json.selectedLayer.layer] = color;
    }
    this.updateView();
  }
  private updateView() {
    this.color_squer.style.backgroundColor = serializeColor(this.getRGB());
    this.color_input_hex.value = serializeColor(this.getRGB());
    const rgb:number[] = set16ToRgb(serializeColor(this.getRGB()))!;
    this.color_input_r.value = rgb[0].toString();
    this.color_input_g.value = rgb[1].toString();
    this.color_input_b.value = rgb[2].toString();
    if(JSON.parse( JSON.stringify ( (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls ) ) !== '{}'){
      (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls['color'] = serializeColor(this.getRGB());
    }else{
      (viewer.selectedLayer.layer as ManagedUserLayer)['shaderControls'] = {};
      (viewer.selectedLayer.layer as ManagedUserLayer).shaderControls['color'] = serializeColor(this.getRGB());
    }
    if(!this.isInitColorBar){
      // 只调用一次
      this.updatePickLineGlobal(rgb);
      this.isInitColorBar = true;
    }
  }
  updatePickLineGlobal(rgb: number[]){
    const init_h = this.getColorBarHeight( rgb );
      this.pickLineY = init_h;
      var col = this.getColorBarPickColor();
      this.updatePickLine(init_h, rgbToHex(col.r, col.g, col.b));
      // var col = this.getColorBarPickColor();
      this.update({r:rgb[0],g:rgb[1],b:rgb[2]});
      this.getGlobalPosition();
  }
  getColorBarHeight(rgb: number[]){
    var h = this.colorBar.width - this.pickLineWidth;
    var oneYV = 255 / (h / 6);
    // 颜色带 1/6 : [255,0,255], 2/6: [0,0,255], 3/6: [0,255,255], 4/6: [0,255,0], 5/6: [255,255,0] 6/6: [255,0,0]
    const new_rgb = [ rgb[0] >= 255 / 2 ? 255 : 0,  rgb[1] >= 255 / 2 ? 255 : 0, rgb[2] >= 255 / 2 ? 255 : 0 ]
    const unit = Math.round(255/6);
    if(new_rgb[0] >= unit * 5 && new_rgb[2] >= unit * 5){
      // 第一格
      return rgb[2] / oneYV;
    }else if( new_rgb[1] >= unit * 5 && new_rgb[2] >= unit * 5){
      // 第三格
      return rgb[1] / oneYV + (h / 3);
    }else if(  new_rgb[0] >= unit * 5 &&  new_rgb[1] >= unit * 5 ){
      // 第五格
      return rgb[0] / oneYV + (4 * h / 6);
    }else if( new_rgb[0] <= unit &&  new_rgb[2] >= unit * 5 && new_rgb[1] <= unit){
      // 第二格
      let min = rgb[0] === 0 ? Math.max(rgb[1], rgb[2]) : 0;
      return min / oneYV + rgb[0] / oneYV + (h / 6);
    }else if(new_rgb[0] <= unit &&  new_rgb[1] >= unit * 5){
      // 第四格
      return rgb[2] / oneYV + (h / 2);
    }else if( new_rgb[0] >= unit * 5 &&  new_rgb[1] <= unit && new_rgb[2] <= unit ){
      // 第六格
      return rgb[0] / oneYV + (5 * h / 6);
    }
    return (rgb[0] / oneYV) + (rgb[1] / oneYV) + (rgb[2] / oneYV);
  }
  getGlobalPosition(){
    // let new_rgb = JSON.parse(JSON.stringify(rgb));
    // new_rgb.sort((a: number,b: number)=> b- a);
    // const index = rgb.indexOf(new_rgb[0]) === 1 ? 'g' : rgb.indexOf(new_rgb[0]) === 2 ? 'b' : 'r';
    // console.log(index)
    // var w = this.scaleWidth;
    // var h = this.scaleHeight;
    // for (var x = 0; x < w; x++) {
    //   for (var y = 0; y < h; y++) {
    //     var col = this.getScaleColor(x, y);
    //     if(Math.abs(col[index] - new_rgb[0]) < 5 ){
          this.global.style.left = this.platterDom.width - 5 + 'px';
          this.global.style.top = -5 + "px";
          this.updateGlobal();
          this.pickPoint = { x: this.platterDom.width - 5, y: 0 };
  //       }
  //     }
  //   }
  }
  private makecolorlist() {
    this.defaultColor.innerHTML = '';
    var ul = makeElement('ul', ['default-color-image-user-layer-ul'], { 'style': 'padding:0px;margin:0' });
    for (let i = 0; i < this.default_color_arr.length; i++) {
      const li = makeElement('li', ['default-color-image-user-layer-li'], { 'color': this.default_color_arr[i].color, 'style': 'background: ' + this.default_color_arr[i].color })
      li.addEventListener('click', () => {
        if (document.getElementsByClassName('default-color-image-user-layer-li-checked')[0]) {
          (ul.getElementsByClassName('default-color-image-user-layer-li-checked')[0] as HTMLElement).classList.remove('default-color-image-user-layer-li-checked');
        }
        li.classList.add('default-color-image-user-layer-li-checked');
        this.isInitColorBar = false;
        this.updateModel(parseRGBColorSpecification(this.default_color_arr[i].color) as Color);
        const _json = viewer.state.toJSON();
        _json.saveSelectedKeyVal = viewer.saveSelectedKeyVal;
        viewer.resetoreJson(_json);
      });
      ul.appendChild(li);
    }
    this.defaultColor.appendChild(makeElement('p',[],{'style': 'text-align: left;margin-bottom: 0.5rem'}, i18n.commonColors));
    this.defaultColor.appendChild(ul);
    this.defaultColor.classList.add('default-color-image-user-layer');
    this.element.appendChild(this.defaultColor);
  }
}

function set16ToRgb(str: string){
  var reg = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/
  if(!reg.test(str)){return;}
  let newStr = (str.toLowerCase()).replace(/\#/g,'')
  let len = newStr.length;
  if(len == 3){
      let t = ''
      for(var i=0;i<len;i++){
          t += newStr.slice(i,i+1).concat(newStr.slice(i,i+1))
      }
      newStr = t
  }
  let arr = []; //将字符串分隔，两个两个的分隔
  for(var i =0;i<6;i=i+2){
      let s = newStr.slice(i,i+2)
      arr.push(parseInt("0x" + s))
  }
  return arr;
}

export function rgbToHex (r: number, g: number, b: number, flag?:boolean) {
  const toHex = (num: number) => {
    const hex = num.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return flag ? `${toHex(r)}${toHex(g)}${toHex(b)}` : `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};